// src/admin/components/PermissionPicker.jsx
//
// The permission checkbox list inside RoleFormModal. Replaces the flat
// <table className="perm-table-checkbox"> that CreateRoleForm.jsx rendered:
// ~38 rows in one unbroken, unsearchable, unsummarized list, which only gets
// worse as the catalog grows (17 modules already).
//
// Three things that list didn't do, and which matter at this size:
//   * filter — type "sow" and see only that module's actions
//   * group by module, with a whole-module toggle, since permissions are
//     granted per module far more often than one action at a time
//   * say how many are selected, without counting checkboxes by eye
//
// Controlled: `value` is the array of selected permission NAMES (what
// CreateRoleRequest.permission_names wants), and every change hands back a
// brand-new array.

import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Search, X } from "lucide-react";
import { Input } from "../../shared/ui";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";

// Groups the catalog by owning module, preserving the server's ordering
// (already alphabetical by name) within each group.
function groupByModule(permissions) {
  const groups = new Map();
  permissions.forEach((p) => {
    if (!groups.has(p.module)) groups.set(p.module, []);
    groups.get(p.module).push(p);
  });
  return [...groups.entries()].map(([module, items]) => ({ module, items }));
}

export default function PermissionPicker({ permissions, value, onChange, disabled = false }) {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);

  const selected = useMemo(() => new Set(value), [value]);

  const groups = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase();
    const matching = needle
      ? permissions.filter(
          (p) =>
            p.name.toLowerCase().includes(needle) ||
            p.description.toLowerCase().includes(needle)
        )
      : permissions;
    return groupByModule(matching);
  }, [permissions, debouncedSearch]);

  function toggleOne(name) {
    onChange(selected.has(name) ? value.filter((n) => n !== name) : [...value, name]);
  }

  // Toggles the module as a whole: any unselected → select all of them;
  // all already selected → clear them. Operates on the module's currently
  // visible (filtered) rows, so it never silently touches hidden ones.
  function toggleModule(items) {
    const names = items.map((p) => p.name);
    const allSelected = names.every((n) => selected.has(n));
    if (allSelected) {
      const dropping = new Set(names);
      onChange(value.filter((n) => !dropping.has(n)));
    } else {
      const merged = new Set(value);
      names.forEach((n) => merged.add(n));
      onChange([...merged]);
    }
  }

  const visibleCount = groups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter permissions…"
            className="!pl-9"
            disabled={disabled}
          />
        </div>

        <div className="flex items-center gap-2 text-xs">
          <span className="font-semibold text-ink">
            {value.length} selected
          </span>
          {value.length > 0 && !disabled && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="inline-flex items-center gap-1 text-muted hover:text-ink"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* 50vh leaves room for the name/level row, description, filter and
          footer inside the dialog's 88vh cap, so the list is the only thing
          that scrolls and the Save button never leaves the viewport. */}
      <div className="border border-line rounded-card max-h-[50vh] overflow-y-auto divide-y divide-line">
        {visibleCount === 0 ? (
          <p className="text-sm text-muted text-center py-8">
            No permission matches “{debouncedSearch}”.
          </p>
        ) : (
          groups.map(({ module, items }) => {
            const allSelected = items.every((p) => selected.has(p.name));
            const someSelected = !allSelected && items.some((p) => selected.has(p.name));

            return (
              <div key={module}>
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 sticky top-0 z-[1]">
                  <span className="text-[11px] font-bold uppercase tracking-wide text-slate-600">
                    {module}
                    {someSelected && (
                      <span className="ml-2 font-semibold normal-case tracking-normal text-brand-700">
                        {items.filter((p) => selected.has(p.name)).length}/{items.length}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggleModule(items)}
                    disabled={disabled}
                    className="text-[11px] font-semibold text-brand-700 hover:text-brand-800 hover:underline disabled:opacity-50"
                  >
                    {allSelected ? "Clear module" : "Select all"}
                  </button>
                </div>

                {items.map((permission) => {
                  const checked = selected.has(permission.name);
                  return (
                    <label
                      key={permission.id}
                      className={[
                        "flex items-start gap-3 px-3 py-2 cursor-pointer transition-colors",
                        checked ? "bg-brand-50/60" : "hover:bg-gray-50",
                        disabled ? "cursor-not-allowed opacity-60" : "",
                      ].join(" ")}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => toggleOne(permission.name)}
                        className="mt-0.5 shrink-0 accent-brand-600 w-4 h-4"
                      />
                      <span className="min-w-0">
                        <span className="block font-mono text-[12.5px] font-medium text-ink break-all">
                          {permission.name}
                        </span>
                        {permission.description && (
                          <span className="block text-xs text-muted mt-0.5">
                            {permission.description}
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

PermissionPicker.propTypes = {
  /** The full catalog, as admin/api/permissionsApi.listPermissions returns it. */
  permissions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      module: PropTypes.string,
    })
  ).isRequired,
  /** Selected permission names. */
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
